import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import {
  X,
  Loader2,
  ShieldCheck,
  AlertTriangle,
  ArrowRight,
  CheckSquare,
  Square,
} from 'lucide-react';

/**
 * Phase 3 — Apply Cashflow Moves modal.
 *
 * Renders a checklist of the deterministic planner's applicable actions
 * (transfer / pay_bill / pay_debt only) + a preview of the before/after
 * balance diff. The user explicitly confirms before any write happens.
 *
 * The dashboard `CashflowGamePlanCard` mounts this modal and is responsible
 * for only opening it when verification.state === "verified" (decisionReady).
 */

const APPLICABLE_TYPES = new Set(['transfer', 'pay_bill', 'pay_debt']);

const fmt = (v) =>
  `$${Number(v || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function DiffRow({ label, before, after, testId }) {
  const delta = (after ?? 0) - (before ?? 0);
  if (Math.abs(delta) < 0.005 && (before == null && after == null)) return null;
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
  const tone = delta > 0
    ? 'text-emerald-600 dark:text-emerald-400'
    : delta < 0
      ? 'text-rose-600 dark:text-rose-400'
      : 'text-gray-500';
  return (
    <div className="flex items-center justify-between gap-3 py-1 text-sm" data-testid={testId}>
      <span className="text-gray-600 dark:text-gray-300">{label}</span>
      <span className="font-mono text-xs flex items-center gap-2">
        <span className="text-gray-500 dark:text-gray-400">{fmt(before)}</span>
        <ArrowRight className="w-3 h-3 text-gray-400" />
        <span className="text-gray-900 dark:text-gray-100 font-semibold">{fmt(after)}</span>
        <span className={`min-w-[60px] text-right ${tone}`}>
          {Math.abs(delta) < 0.005 ? '' : `${sign}${fmt(Math.abs(delta))}`}
        </span>
      </span>
    </div>
  );
}

export default function CashflowApplyModal({ plan, scenario, onClose, onApplied }) {
  const applicableActions = useMemo(
    () => (plan?.actions || []).filter((a) => APPLICABLE_TYPES.has(a.actionType) && Number(a.amount) > 0),
    [plan],
  );

  // Default every applicable action checked so the user is one click away
  // from applying a "trust the planner" plan.
  const [selected, setSelected] = useState(() => new Set(applicableActions.map((a) => a.step)));
  const [diff, setDiff] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const toggleStep = (step) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(step)) next.delete(step);
      else next.add(step);
      return next;
    });
  };

  // Lookup account names so the diff rows render with the user's labels
  // instead of internal ids.
  const accountNameById = useMemo(() => {
    const map = {};
    for (const a of scenario?.accounts || []) map[a.id] = a.name || a.id;
    return map;
  }, [scenario]);

  const fetchPreview = useCallback(async (signal) => {
    if (selected.size === 0) {
      setDiff(null);
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const res = await axios.post(
        `${API}/cashflow/scenario/apply-moves`,
        { stepIds: [...selected], dryRun: true },
        { signal },
      );
      setDiff(res.data);
    } catch (err) {
      if (axios.isCancel?.(err) || err?.name === 'CanceledError') return;
      console.error('Apply preview failed:', err);
      setPreviewError(err?.response?.data?.detail || 'Could not load preview.');
      setDiff(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [selected]);

  // Debounce preview refresh as the user toggles checkboxes.
  useEffect(() => {
    const ctrl = new AbortController();
    const t = setTimeout(() => fetchPreview(ctrl.signal), 250);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [fetchPreview]);

  async function handleCommit() {
    if (selected.size === 0) {
      toast.error('Select at least one move to apply.');
      return;
    }
    if (!window.confirm(
      `Apply ${selected.size} move(s) to your balances?\n\n` +
      `This will update PaperTrail's actual balances and your saved Cashflow scenario, and re-verify your balances as of today.`
    )) return;
    setCommitting(true);
    try {
      const res = await axios.post(`${API}/cashflow/scenario/apply-moves`, {
        stepIds: [...selected],
        dryRun: false,
      });
      toast.success(`${res.data.actions_to_apply?.length || 0} move(s) applied. Balances re-verified today.`);
      onApplied?.(res.data);
      onClose?.();
    } catch (err) {
      console.error('Apply commit failed:', err);
      toast.error(err?.response?.data?.detail || 'Apply failed. Please try again.');
    } finally {
      setCommitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      data-testid="cashflow-apply-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-2xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-500" />
              Apply Cashflow Moves
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 max-w-xl">
              Select the moves you want to apply. The diff updates live as you toggle. Nothing is written to your balances until you confirm.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800"
            data-testid="cashflow-apply-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-5">
          {applicableActions.length === 0 ? (
            <div className="rounded-md border border-gray-200 dark:border-gray-700 px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400" data-testid="cashflow-apply-empty">
              No applicable moves in the current plan. Deposits and informational steps can't be auto-applied.
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  Moves to apply ({selected.size} of {applicableActions.length} selected)
                </p>
                <div className="flex gap-2 text-[11px]">
                  <button
                    type="button"
                    onClick={() => setSelected(new Set(applicableActions.map((a) => a.step)))}
                    className="text-emerald-700 dark:text-emerald-400 hover:underline"
                    data-testid="cashflow-apply-select-all"
                  >Select all</button>
                  <button
                    type="button"
                    onClick={() => setSelected(new Set())}
                    className="text-gray-500 hover:underline"
                    data-testid="cashflow-apply-clear-all"
                  >Clear</button>
                </div>
              </div>
              <ul className="space-y-1.5" data-testid="cashflow-apply-action-list">
                {applicableActions.map((a) => {
                  const isOn = selected.has(a.step);
                  return (
                    <li key={a.step}>
                      <button
                        type="button"
                        onClick={() => toggleStep(a.step)}
                        className={`w-full text-left flex items-start gap-3 rounded-md border px-3 py-2 transition ${
                          isOn
                            ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-700/60 dark:bg-emerald-900/20'
                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                        data-testid={`cashflow-apply-action-${a.step}`}
                        data-action-type={a.actionType}
                      >
                        {isOn ? <CheckSquare className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
                              : <Square className="w-4 h-4 mt-0.5 text-gray-400 shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {a.label}
                            <span className="text-gray-400 font-normal"> — {fmt(a.amount)}</span>
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 leading-snug mt-0.5">
                            {a.reason}
                          </p>
                          <span className="inline-block mt-1 text-[10px] uppercase tracking-wide text-gray-500">
                            {a.actionType.replace(/_/g, ' ')}
                          </span>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="rounded-md border border-gray-200 dark:border-gray-700 px-4 py-3" data-testid="cashflow-apply-preview">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Before → After</p>
              {previewLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
            </div>
            {previewError ? (
              <p className="text-xs text-rose-600 dark:text-rose-400 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> {previewError}
              </p>
            ) : !diff || selected.size === 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">Select moves above to see the projected diff.</p>
            ) : (
              <>
                <div className="space-y-0.5">
                  <DiffRow label="Personal Checking (PaperTrail)" before={diff.before_actual?.personal} after={diff.after_actual?.personal} testId="diff-actual-personal" />
                  <DiffRow label="Business Checking (PaperTrail)" before={diff.before_actual?.business} after={diff.after_actual?.business} testId="diff-actual-business" />
                  <DiffRow label="Savings (PaperTrail)"          before={diff.before_actual?.savings}  after={diff.after_actual?.savings}  testId="diff-actual-savings" />
                </div>
                {Object.keys(diff.scenario_deltas || {}).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-dashed border-gray-200 dark:border-gray-700">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Cashflow scenario accounts</p>
                    <div className="space-y-0.5">
                      {Object.entries(diff.scenario_deltas).map(([aid, _delta]) => (
                        <DiffRow
                          key={aid}
                          label={accountNameById[aid] || aid}
                          before={diff.before_scenario_accounts?.[aid]}
                          after={diff.after_scenario_accounts?.[aid]}
                          testId={`diff-scenario-${aid}`}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {diff.warnings && diff.warnings.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-dashed border-gray-200 dark:border-gray-700">
                    <p className="text-[11px] uppercase tracking-wide text-amber-600 mb-1">Notes</p>
                    <ul className="text-xs text-amber-700 dark:text-amber-300 list-disc pl-5 space-y-0.5">
                      {diff.warnings.map((w) => (<li key={w}>{w}</li>))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            disabled={committing}
            className="px-3 py-1.5 rounded-md text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 disabled:opacity-60"
            data-testid="cashflow-apply-cancel"
          >Cancel</button>
          <button
            type="button"
            onClick={handleCommit}
            disabled={committing || selected.size === 0 || !diff || !!previewError}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-60"
            data-testid="cashflow-apply-confirm"
          >
            {committing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Confirm &amp; apply
          </button>
        </div>
      </div>
    </div>
  );
}
